import { inject, Injectable } from '@angular/core';
import { Resolve, Router } from '@angular/router';
import { LoadingService } from '@portal/loading/data-access';
import { catchError, finalize, mergeMap, take, throwError, timer, withLatestFrom } from 'rxjs';
import { ROUTE } from '../constant/route.constant';
import { RouteErrorCode } from '../type/route.type';
import { BizflexFacade } from '../store';
import { SignOffTypeParam } from '../type/sign-off.type';
import { PersonnelType } from '@core/data-access';
import { ApplicationApiService } from '../api/application-api.service';

@Injectable({ providedIn: 'root' })
export class SignOffResolver implements Resolve<boolean> {
  private readonly applicationApi = inject(ApplicationApiService);
  private readonly bizflexFacade = inject(BizflexFacade);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);

  resolve() {
    this.loadingService.show();

    return this.bizflexFacade.signToken$.pipe(
      take(1),
      mergeMap(token => {
        if (!token) return throwError(() => new Error('Sign token is not available'));
        return timer(3000).pipe(mergeMap(() => this.applicationApi.getContract(token)));
      }),
      withLatestFrom(this.bizflexFacade.jwt$.pipe(take(1)), this.bizflexFacade.directors$.pipe(take(1))),
      mergeMap(([contract, jwt, directors]) => {
        if (!contract) return throwError(() => new Error('Contract is not available'));
        if (contract['status'] === 'completed') {
          return this.router.navigate([ROUTE.SUCCESS.MAIN, ROUTE.SUCCESS.APPROVED]);
        }

        if (!jwt) return throwError(() => new Error('JWT is not available'));

        if (jwt?.type === PersonnelType.entity) {
          /**
           * Single Director: ROUTE.SUCCESS.MAIN, ROUTE.SUCCESS.APPROVED
           * Multiple Director: ROUTE.SUCCESS.MAIN, ROUTE.SUCCESS.SIGN_OFF, SignOffTypeParam.Multi
           */
          return this.router.navigate([
            ROUTE.SUCCESS.MAIN,
            ...(directors && directors.length > 1 ? [ROUTE.SUCCESS.SIGN_OFF, SignOffTypeParam.Multi] : [ROUTE.SUCCESS.APPROVED]),
          ]);
        }

        return this.router.navigate([ROUTE.SUCCESS.MAIN, ROUTE.SUCCESS.PROCESSED]);
      }),
      catchError(() => this.navigateErrorPage()),
      finalize(() => this.loadingService.hide()),
    );
  }

  private navigateErrorPage(errorCode?: RouteErrorCode): Promise<boolean> {
    return this.router.navigate([ROUTE.ERROR.MAIN, errorCode].filter(Boolean));
  }
}
